import moment from "moment";

const ALERT_SPEC_FLAGS = new Set(["2", "7", "8"]);

const getReviewResultsExpiryData = (report) => (
  report?.review_results_expiry_data && typeof report.review_results_expiry_data === "object"
    ? report.review_results_expiry_data
    : null
);

export const hasReportAlertRemaining = (report) => (
  Boolean(getReviewResultsExpiryData(report)?.warning_flag)
);

export const formatSpecAlertLabel = (specsFlag, label, report) => (
  ALERT_SPEC_FLAGS.has(String(specsFlag)) && hasReportAlertRemaining(report)
    ? `${label} (Alert)`
    : label
);

export const getReportAlertExpirationUtc = (report) => (
  getReviewResultsExpiryData(report)?.review_results_expiry_date || null
);

/**
 * Calculation uses full date+time (UTC → local) so FE aligns with BE timestamp logic.
 * Display remains days-only (Due today, 1 or 2 day remaining); tooltip maps to today/24/48 hours.
 */
export const getAlertDaysRemaining = (expirationDateUtc, now = moment()) => {
  if (!expirationDateUtc) {
    return null;
  }
  const expirationMoment = moment.utc(expirationDateUtc).local();
  if (!expirationMoment.isValid()) {
    return null;
  }
  if (expirationMoment.isSameOrBefore(now, "day")) {
    return 0;
  }
  const hoursLeft = expirationMoment.diff(now, "hours", true);
  const daysUntil = Math.ceil(hoursLeft / 24);
  return Math.min(2, Math.max(1, daysUntil));
};

export const formatAlertDaysRemainingLabel = (daysRemaining) => {
  if (daysRemaining == null) {
    return "";
  }
  if (daysRemaining === 0) {
    return "Due today";
  }
  const dayWord = daysRemaining === 1 ? "day" : "days";
  return `${daysRemaining} ${dayWord} remaining`;
};

export const formatAlertRemainingTooltip = (daysRemaining) => {
  if (daysRemaining == null) {
    return "";
  }
  if (daysRemaining === 0) {
    return "Decision required: Results will be auto-released today.";
  }
  const hours = daysRemaining <= 1 ? 24 : 48;
  return `Decision required: Results auto-release in ${hours} hours.`;
};

export const getReportAlertRemainingDisplay = (report, now = moment()) => {
  if (!hasReportAlertRemaining(report)) {
    return null;
  }
  const expirationDateUtc = getReportAlertExpirationUtc(report);
  if (!expirationDateUtc) {
    return null;
  }
  const daysRemaining = getAlertDaysRemaining(expirationDateUtc, now);
  if (daysRemaining == null) {
    return null;
  }
  return {
    daysRemaining,
    label: formatAlertDaysRemainingLabel(daysRemaining),
    tooltipTitle: formatAlertRemainingTooltip(daysRemaining),
  };
};
